<template>
    <div id="app">
        <Transition
            leave-active-class="transition ease-in duration-200"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
        >
            <OverlayLoading
                v-if="overlays.initialLoad"
                data-qa="loading overlay"
            />
        </Transition>
        <router-view v-if="!overlays.initialLoad"/>
        <WebViewTimeLimitModalManager v-if="isWebView"/>
    </div>
</template>

<script setup>
import { useGtm } from '@gtm-support/vue-gtm';
import { useRouter } from 'vue-router';
import { provide, watch } from 'vue';
import { useDropZone } from '@vueuse/core';
import { enableTrackers } from '../utils/tracking';
import { useHeartbeat } from '../composables/heartbeat';
import config from '../config';
import { useJobDataStore } from '../stores/job-store';
import { hexToRgb } from '../utils/colors';
import OverlayLoading from './organisms/overlay/OverlayLoading.vue';
import { useOverlays } from '../stores/overlays-store';
import { useWebViewContext } from '../composables/webview-context';
import WebViewTimeLimitModalManager from './webview/WebViewTimeLimitModalManager.vue';

const router = useRouter();
const overlays = useOverlays();
const { isWebView } = useWebViewContext();

if (!isWebView.value) {
    useHeartbeat();
}

function enableTracking() {
    const trackerDependencies = [];
    if (config.gtm.active) {
        const VueGtm = useGtm();
        trackerDependencies.push(VueGtm);
    }
    enableTrackers(trackerDependencies);
}

/* Update branding variables */
const jobData = useJobDataStore();
function updateBrandingCSSVars(branding) {
    const root = document.documentElement;
    if (branding?.color) root.style.setProperty('--branding-color', hexToRgb(branding.color));
    if (branding?.headerImage) root.style.setProperty('--branding-image', `url(${branding.headerImage})`);
}
watch(() => jobData.company.branding, updateBrandingCSSVars, { immediate: true, deep: true });

const { isOverDropZone } = useDropZone(document.body);
provide('showDropzones', isOverDropZone);

router.isReady().then(enableTracking);
</script>

<style lang="scss">
@use '~@karriereat/global-styles/scss/tools/typo';
@use '~@karriereat/global-styles/scss/generic/all'; // Set correct font and base settings.
@use '~@karriereat/global-styles/scss/components/action-box/index' as actionBox;
@use '~@karriereat/global-styles/scss/components/badge/index' as badge;
@use '~@karriereat/global-styles/scss/components/brandline/index' as brandline;
@use '~@karriereat/global-styles/scss/components/button/index' as button;
@use '~@karriereat/global-styles/scss/components/card/index' as card;
@use '~@karriereat/global-styles/scss/components/checkbox/index' as checkbox;
@use '~@karriereat/global-styles/scss/components/circular-progress-indicator/index' as progress;
@use '~@karriereat/global-styles/scss/components/cupcake/index' as cupcake;
@use '~@karriereat/global-styles/scss/components/divider/index' as divider;
@use '~@karriereat/global-styles/scss/components/dropdown/index' as dropdown;
@use '~@karriereat/global-styles/scss/components/excerpt/index' as excerpt;
@use '~@karriereat/global-styles/scss/components/headline/index' as headline;
@use '~@karriereat/global-styles/scss/components/info-box/index' as infoBox;
@use '~@karriereat/global-styles/scss/components/input/index' as input;
@use '~@karriereat/global-styles/scss/components/label/index' as label;
@use '~@karriereat/global-styles/scss/components/list/index' as list;
@use '~@karriereat/global-styles/scss/components/link/index' as link;
@use '~@karriereat/global-styles/scss/components/loading-dots/index' as loadingDots;
@use '~@karriereat/global-styles/scss/components/logo/index' as logo;
@use '~@karriereat/global-styles/scss/components/modal/index' as modal;
@use '~@karriereat/global-styles/scss/components/navbar/index' as navbar;
@use '~@karriereat/global-styles/scss/components/text-divider/index' as textDivider;
@use '~@karriereat/global-styles/scss/components/upload-box/index' as uploadBox;
@use '~@karriereat/global-styles/scss/objects/ladder/index' as ladder;
@use '../assets/scss/utilities/ellipsis';
@use '../assets/scss/utilities/screenReaderOnly';

html {
    overflow-y: scroll;
}

body {
    @include typo.k-typo-m();

    // text will look more like designs, esp. on dark backgrounds text will look lighter
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
}

strong {
    font-weight: 500;
}

.has-status-error {
    @apply scroll-mt-2xl;
}

/* Override global styles focus behavior because it is unreliable when used with the programmatic focusing
    of headless ui. Use the headless ui data attribute instead */
*:focus-visible,
*:focus-visible:focus {
    @apply outline-none;
}

[data-headlessui-focus-visible] *:focus {
    @apply outline outline-2 outline-blue-900;
    border-radius: var(--k-rounded);
}
</style>
